import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, EyeOff, Moon, Sun } from "lucide-react";
import "../global.css";
import { useAuth } from "../contexts/AuthContext";
import { authAPI } from "../services/api";
import { persistAttributionFromUrl, getAttribution } from "../utils/attribution";

const formatCpfCnpj = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 11) return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1/$2").replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const formatPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

const Register = () => {
  const { darkMode, setDarkMode } = useTheme();
  const [formData, setFormData] = useState({ nome: "", email: "", senha: "", cpf_cnpj: "", telefone: "", nome_empresa: "" });
  const [aceitouTermos, setAceitouTermos] = useState(false);
  const [displayCpf, setDisplayCpf] = useState("");
  const [displayPhone, setDisplayPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  // Persiste atribuição vinda da URL (anúncio ou link de vendedor) assim que a página carrega
  useEffect(() => { persistAttributionFromUrl(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const digits = formData.cpf_cnpj.replace(/\D/g, "");
    if (digits && digits.length !== 11 && digits.length !== 14) return toast.error("CPF deve ter 11 dígitos ou CNPJ 14 dígitos");
    const phoneDigits = formData.telefone.replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 11) return toast.error("Telefone inválido");
    if (!aceitouTermos) return toast.error("Você precisa aceitar os termos de uso e política de privacidade");

    setLoading(true);
    try {
      const attribution = getAttribution();
      const payload = { ...formData, cpf_cnpj: digits || undefined, telefone: phoneDigits, ...attribution };
      const res = await authAPI.register(payload);
      const { token, usuario, cliente } = res.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(usuario));

      // Dispara conversão para Google Ads quando o ID está configurado
      const conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
      if (conversionId && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: conversionId,
          transaction_id: cliente?.id || usuario?.id,
        });
      }

      // Dispara evento GA4 que a conversão "Inscrição" do Google Ads escuta
      if (typeof window.gtag === "function") {
        window.gtag("event", "ads_conversion_signup", {
          cliente_id: cliente?.id || usuario?.id,
          utm_source: attribution?.utm_source || null,
          utm_medium: attribution?.utm_medium || null,
          utm_campaign: attribution?.utm_campaign || null,
          vendedor_codigo: attribution?.vendedor_codigo || null,
        });
      }

      toast.success("Conta criada com sucesso! Bem-vindo à Nomoz 🚀");
      window.location.href = "/";
    } catch (err) {
      const msg = err.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg[0] : msg || "Erro ao criar conta");
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cpf_cnpj") {
      setDisplayCpf(formatCpfCnpj(value));
      setFormData({ ...formData, cpf_cnpj: value.replace(/\D/g, "") });
    } else if (name === "telefone") {
      setDisplayPhone(formatPhone(value));
      setFormData({ ...formData, telefone: value.replace(/\D/g, "") });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const inputClass = darkMode
    ? "bg-zinc-600 border border-gray-700 text-white focus:ring-green-500 focus:border-green-500"
    : "bg-white border border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500";
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? "text-white" : "text-gray-900"}`;

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${darkMode ? "content-bg-dark" : "bg-gray-100"}`}>
      <button onClick={() => setDarkMode(!darkMode)}
        className={`fixed top-4 right-4 p-2 rounded-full ${darkMode ? "bg-gray-800 text-gray-200 hover:text-white hover:bg-gray-700" : "bg-white text-gray-400 hover:text-gray-500"} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}>
        {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>

      <div className="max-w-md w-full space-y-6">
        <div className="mx-auto flex items-center justify-center">
          <img src="/assets/logo_nomoz.png" width={177} height={65} alt="Nomoz Logo" />
        </div>
        <div className="text-center">
          <h2 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>Crie sua conta grátis</h2>
          <p className={`mt-1 text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Teste a plataforma por 7 dias sem compromisso</p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className={labelClass}>Nome completo *</label>
            <input name="nome" type="text" required className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`} value={formData.nome} onChange={handleChange} />
          </div>

          <div>
            <label className={labelClass}>CPF ou CNPJ (opcional)</label>
            <input name="cpf_cnpj" type="text" className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
              placeholder="000.000.000-00" value={displayCpf} onChange={handleChange} />
            <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Será solicitado antes do pagamento</p>
          </div>

          {formData.cpf_cnpj.length >= 12 && (
            <div>
              <label className={labelClass}>Nome da empresa *</label>
              <input name="nome_empresa" type="text" required className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`} value={formData.nome_empresa} onChange={handleChange} />
            </div>
          )}

          <div>
            <label className={labelClass}>Email *</label>
            <input name="email" type="email" required className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`} value={formData.email} onChange={handleChange} />
          </div>

          <div>
            <label className={labelClass}>Telefone / WhatsApp *</label>
            <input name="telefone" type="tel" required className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`}
              placeholder="(11) 99999-9999" value={displayPhone} onChange={handleChange} />
          </div>

          <div className="relative">
            <label className={labelClass}>Senha *</label>
            <input name="senha" type={showPassword ? "text" : "password"} required minLength={8} className={`block w-full rounded-md shadow-sm py-2 px-3 ${inputClass}`} value={formData.senha} onChange={handleChange} />
            <button type="button" className="absolute right-3 top-8" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
            </button>
            <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Mínimo 8 caracteres, maiúscula, minúscula, número e especial</p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={aceitouTermos} onChange={(e) => setAceitouTermos(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-nomoz-orange focus:ring-nomoz-orange shrink-0" />
            <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Li e aceito os{" "}
              <a href="https://nomoz.tech/termos" target="_blank" rel="noopener noreferrer" className="text-nomoz-orange hover:underline">Termos de Uso</a>
              {" "}e a{" "}
              <a href="https://nomoz.tech/politica" target="_blank" rel="noopener noreferrer" className="text-nomoz-orange hover:underline">Política de Privacidade</a>
            </span>
          </label>

          <button type="submit" disabled={loading || !aceitouTermos}
            className="gradient-button group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : "Criar conta grátis"}
          </button>

          <div className="text-center">
            <Link to="/login" className={`text-sm ${darkMode ? "text-nomoz-orange hover:text-orange-400" : "text-nomoz-orange hover:text-orange-600"}`}>
              Já tem uma conta? Faça login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
